import { Link } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { DashboardSection } from '../components/DashboardSection.tsx'
import { upsertTickersBatch, type TickerImportRecord } from '../api/tickers.ts'
import {
  type ParsedInput,
  SEPARATOR_CANDIDATES,
  TICKER_IMPORT_FIELDS,
  HEADER_HINTS_BY_FIELD,
  normalizeLabel,
  parseMappingText,
  separatorToMappingValue,
} from '../utils/importUtils.ts'
import { useCurrentUser } from '../hooks/useCurrentUser.ts'
import { useCategoriesTickersExport } from '../hooks/useCategoriesTickersExport.ts'
import { useCategoriesTickersImport } from '../hooks/useCategoriesTickersImport.ts'
import { copyTextToClipboard } from '../lib/copyTextToClipboard.ts'
import styles from './Import.module.css'

type SectionKey = 'export' | 'import'

export function ImportPage() {
  const { userId } = useCurrentUser()
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>({
    export: true,
    import: true,
  })
  const [exportText, setExportText] = useState('')
  const [exportFeedback, setExportFeedback] = useState('')
  const [rawText, setRawText] = useState('')
  const [mappingText, setMappingText] = useState('')
  const [hasEditedMapping, setHasEditedMapping] = useState(false)
  const [isExecutingImport, setIsExecutingImport] = useState(false)
  const [executeFeedback, setExecuteFeedback] = useState('')
  const {
    generateExport,
    isLoading: isLoadingExportData,
    error: exportError,
  } = useCategoriesTickersExport(userId)
  const { executeImport: executeCategoriesTickersImport, isImporting: isImportingCategoriesTickers } =
    useCategoriesTickersImport(userId)

  function toggleSection(section: SectionKey) {
    setOpenSections((current) => ({
      ...current,
      [section]: !current[section],
    }))
  }

  // Parser base do texto colado: detecta separador, headers e linhas de dados.
  const parsedInput = useMemo<ParsedInput>(() => {
    const lines = rawText.split(/\r?\n/).filter((line) => line.trim().length > 0)

    if (lines.length === 0) {
      return {
        lineCount: 0,
        separator: null,
        headers: [],
        rows: [],
      }
    }

    const firstLine = lines[0]
    const separator = SEPARATOR_CANDIDATES.find(
      (candidate) => firstLine.split(candidate.char).length > 1,
    ) ?? null

    const headers = separator
      ? firstLine.split(separator.char).map((header) => header.trim())
      : []

    const rows =
      separator == null
        ? []
        : lines.slice(1).map((line) => line.split(separator.char).map((cell) => cell.trim()))

    return {
      lineCount: lines.length,
      separator,
      headers,
      rows,
    }
  }, [rawText])

  // Console de diagnóstico da etapa de leitura.
  const consoleOutput = useMemo(() => {
    const { lineCount, separator, headers } = parsedInput

    if (!rawText.trim()) {
      return [
        '[aguardando] Cole os dados para iniciar leitura.',
        '[aguardando] Separador detectado: -',
        '[aguardando] Headers detectados: -',
      ].join('\n')
    }

    return [
      `[ok] Linhas recebidas: ${lineCount}`,
      separator
        ? `[ok] Separador detectado: ${separator.label}`
        : '[erro] Separador detectado: nenhum separador conhecido',
      headers.length > 0
        ? `[ok] Headers detectados (${headers.length}): ${headers.join(' | ')}`
        : '[erro] Headers detectados: nao foi possivel extrair',
    ].join('\n')
  }, [parsedInput, rawText])

  // Template inicial de mapeamento com tentativas por cabeçalhos conhecidos.
  const mappingTemplate = useMemo(() => {
    const normalizedHeaders = parsedInput.headers.map((header) => ({
      original: header,
      normalized: normalizeLabel(header),
    }))

    const mappingLines = TICKER_IMPORT_FIELDS.map((field) => {
      const hints = HEADER_HINTS_BY_FIELD[field].map(normalizeLabel)
      const matchedHeader = normalizedHeaders.find((header) =>
        hints.includes(header.normalized),
      )?.original

      return `${field}=${matchedHeader ?? ''}`
    })

    const compactFields = TICKER_IMPORT_FIELDS.join(', ')
    const compactHeaders =
      parsedInput.headers.length > 0 ? parsedInput.headers.join(' | ') : '-'
    const separatorLabel = parsedInput.separator?.label ?? '-'

    return [
      `# campos_tickers: ${compactFields}`,
      `# headers_detectados: ${compactHeaders}`,
      `# separador_detectado: ${separatorLabel}`,
      `# user_id_atual: ${userId || '-'}`,
      `separator=${separatorToMappingValue(parsedInput.separator)}`,
      ...mappingLines,
    ].join('\n')
  }, [parsedInput.headers, parsedInput.separator, userId])

  useEffect(() => {
    // Mantém o template sincronizado até a primeira edição manual.
    if (hasEditedMapping) return
    setMappingText(mappingTemplate)
  }, [hasEditedMapping, mappingTemplate])

  // Converte linhas da planilha em payload pronto para upsert.
  const mappedRecords = useMemo<TickerImportRecord[]>(() => {
    if (!rawText.trim()) {
      return []
    }

    if (!parsedInput.separator || parsedInput.headers.length === 0) {
      return []
    }

    if (parsedInput.rows.length === 0) {
      return []
    }

    const mapping = parseMappingText(mappingText)
    const headerIndexes = new Map<string, number>()
    parsedInput.headers.forEach((header, index) => {
      headerIndexes.set(normalizeLabel(header), index)
    })

    return parsedInput.rows
      .map((row) => {
        const record: TickerImportRecord = { user_id: userId, ticker: '' }

        for (const field of TICKER_IMPORT_FIELDS) {
          const mappedHeader = mapping.get(field)
          if (!mappedHeader) continue
          const headerIndex = headerIndexes.get(normalizeLabel(mappedHeader))
          if (headerIndex == null) continue
          const cellValue = (row[headerIndex] ?? '').trim()
          if (!cellValue) continue

          if (field === 'ticker') {
            record.ticker = cellValue.toUpperCase()
            continue
          }

          record[field] = cellValue
        }

        return record
      })
      .filter((record) => record.ticker.length > 0)
  }, [mappingText, parsedInput, rawText, userId])

  // Preview textual do payload final.
  const mappedPortfolioPreview = useMemo(() => {
    if (!rawText.trim()) {
      return '[aguardando] Cole os dados e ajuste o mapeamento para gerar preview.'
    }

    if (!parsedInput.separator || parsedInput.headers.length === 0) {
      return '[erro] Sem separador/headers validos para montar preview.'
    }

    if (parsedInput.rows.length === 0) {
      return '[aviso] Nenhuma linha de dados encontrada para preview.'
    }

    const lines: string[] = [
      `[ok] Registros montados: ${mappedRecords.length}`,
      `[info] user_id atual aplicado: ${userId || '-'}`,
      ...mappedRecords.map((record, index) => `${index + 1}. ${JSON.stringify(record)}`),
    ]

    if (executeFeedback) {
      lines.unshift('', executeFeedback)
    }

    return lines.join('\n')
  }, [executeFeedback, mappedRecords, parsedInput, rawText, userId])

  async function handleExecuteImport() {
    if (mappedRecords.length === 0 || isExecutingImport) return

    setIsExecutingImport(true)
    setExecuteFeedback('[processando] Executando upsert em lote...')

    try {
      // Dispara o upsert em lote no backend.
      const response = await upsertTickersBatch(mappedRecords)
      setExecuteFeedback(
        `[ok] Upsert concluido: ${response.upserted} registro(s) processado(s).`,
      )
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro desconhecido'
      setExecuteFeedback(`[erro] Falha no upsert: ${message}`)
    } finally {
      setIsExecutingImport(false)
    }
  }

  async function handleGenerateExport() {
    if (isLoadingExportData) return
    if (exportError) {
      setExportFeedback(`[erro] Nao foi possivel carregar os dados do export: ${exportError}`)
      return
    }

    const nextExportText = generateExport()
    setExportText(nextExportText)

    try {
      await copyTextToClipboard(nextExportText)
      setExportFeedback('[ok] Export gerado e copiado para a area de transferencia.')
    } catch (error) {
      console.error(error)
      setExportFeedback(
        '[erro] Export gerado, mas nao foi possivel copiar para a area de transferencia.',
      )
    }
  }

  async function handleImportCategoriesTickers() {
    const result = await executeCategoriesTickersImport(exportText)
    setExportFeedback(result)
  }

  return (
    <div className={`${styles.page} ${styles.pageMinimal}`}>
      <header className={`${styles.header} ${styles.headerMinimal}`}>
        <div className={styles.headerTitleRow}>
          <h1>Importar carteira</h1>
          <Link className={styles.backLink} to="/dashboard">
            Dashboard
          </Link>
        </div>
        {/*
        <p className={styles.subtitle}>
          Cole os dados da planilha (copiar/colar direto da tabela).
        </p>
        */}
      </header>

      <main className={styles.main}>
        <DashboardSection
          title="Exportar/Importar categorias e tickers"
          isOpen={openSections.export}
          onToggle={() => toggleSection('export')}
          contentId="import-export-section"
        >
          <div className={styles.modalBody}>
            <div className={`${styles.field} ${styles.exportField}`}>
              <div className={styles.importPreviewHead}>
                <label htmlFor="export-categories-tickers-text">
                  Arquivo para exportacao/importacao
                </label>
                <div className={styles.actionButtons}>
                  <button
                    className={`${styles.btn} ${styles.btnGhost}`}
                    type="button"
                    disabled={isLoadingExportData || isImportingCategoriesTickers}
                    onClick={handleGenerateExport}
                  >
                    {isLoadingExportData ? 'Carregando...' : 'Exportar'}
                  </button>
                  <button
                    className={`${styles.btn} ${styles.btnGhost}`}
                    type="button"
                    disabled={!exportText.trim() || isImportingCategoriesTickers}
                    onClick={handleImportCategoriesTickers}
                  >
                    {isImportingCategoriesTickers ? 'Importando...' : 'Importar'}
                  </button>
                </div>
              </div>
              <textarea
                id="export-categories-tickers-text"
                className={styles.importTextarea}
                value={exportText}
                onChange={(event) => setExportText(event.target.value)}
                placeholder="Cole aqui o arquivo de categorias e tickers, ou clique em Exportar para gerar e copiar."
                spellCheck={false}
              />
              {exportFeedback ? (
                <div className={styles.feedbackText}>{exportFeedback}</div>
              ) : exportError ? (
                <div className={styles.feedbackText}>
                  [erro] Nao foi possivel carregar os dados do export: {exportError}
                </div>
              ) : null}
            </div>
          </div>
        </DashboardSection>

        <DashboardSection
          title="Importar carteira"
          isOpen={openSections.import}
          onToggle={() => toggleSection('import')}
          contentId="import-section"
        >
          <div className={styles.modalBody}>
            <div className={styles.field}>
              <textarea
                id="raw-import-text"
                className={`${styles.importTextarea} ${styles.rawImportText}`}
                value={rawText}
                onChange={(event) => setRawText(event.target.value)}
                rows={12}
                placeholder={"Cole os dados da carteira aqui.\nCopiar e colar direto da tabela ou planilha, incluindo a linha de headers.\nPode usar CTRL+A, CTRL+C e CTRL+V."}
                spellCheck={false}
              />
            </div>

            <div className={styles.field}>
              <textarea
                id="import-console"
                className={`${styles.importConsole} ${styles.importTextarea}`}
                value={consoleOutput}
                readOnly
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="mapping-text">
                Mapeamento em texto (campo_ticker=header_detectado)
              </label>
              <textarea
                id="mapping-text"
                className={`${styles.mappingText} ${styles.importTextarea}`}
                value={mappingText}
                onChange={(event) => {
                  setHasEditedMapping(true)
                  setMappingText(event.target.value)
                }}
                rows={8}
                spellCheck={false}
              />
            </div>

            <div className={`${styles.field} ${styles.importPreviewField}`}>
              <div className={styles.importPreviewHead}>
                <label htmlFor="mapped-portfolio-preview">
                  Carteira pronta para importar
                </label>
                <button
                  className={`${styles.btn} ${styles.btnGhost}`}
                  type="button"
                  disabled={mappedRecords.length === 0 || isExecutingImport}
                  onClick={handleExecuteImport}
                >
                  <span className={styles.iconPlay} aria-hidden="true">
                    <svg viewBox="0 0 10 10" focusable="false">
                      <path d="M2 1.5 8 5 2 8.5z" />
                    </svg>
                  </span>
                  {isExecutingImport ? 'Executando...' : 'Executar'}
                </button>
              </div>
              <textarea
                id="mapped-portfolio-preview"
                className={`${styles.importConsole} ${styles.importTextarea}`}
                value={mappedPortfolioPreview}
                rows={9}
                readOnly
              />
            </div>
          </div>
        </DashboardSection>
      </main>
    </div>
  )
}
